import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  notification,
  message,
  Tooltip,
  Skeleton,
} from "antd";
import ProjectSettingToggle from "./ProjectSettingToggle";
import { useQuery, useMutation } from "@tanstack/react-query";
import { fetchProjectUsers } from "../services/projectsAPI";
import {
  updateSubmittal,
  fetchNextSubmittalNumber,
  createSubmittalCoversheet,
} from "../services/submittalsAPI";
import { useAuth } from "../hooks/useAuth";
import {
  toUTCMidnight,
  parseDateOnlyForPicker,
  calculateDateFromLeadTime,
  calculateLeadTimeWeeks,
} from "../utils/dateUtils";
import { DATE_PICKER_FORMAT } from "../constants/dateConstants";
import queryClient from "../queryClient";
import {
  filterOption,
  handleIntegerKeyDown,
  createIntegerChangeHandler,
} from "../utils/textUtils";
import { CloseOutlined } from "@ant-design/icons";
import AttachmentsSection from "./AttachmentsSection";
import SimpleRichTextEditor from "./SimpleRichTextEditor";
import { fetchSpecSections } from "../services/specSectionsAPI";
import { fetchVendors } from "../services/vendorsAPI";
import { getSubmittalById } from "../services/submittalsAPI";
import { normalizeBooleanValue } from "../utils/projectUtils";
import ProjectUserSelect from "./ProjectUserSelect";
import SpecSectionSelect from "./SpecSectionSelect";
import {
  COVERSHEET_REQUIRES_PDF_TOOLTIP,
  hasPdfAttachments,
} from "../utils/attachmentUtils";

const UpdateSubmittalModal = ({ visible, onClose, submittalId }) => {
  const { currentUser, selectedProject } = useAuth();
  const projectId = selectedProject.project_id;
  const [form] = Form.useForm();
  const [reviewSteps, setReviewSteps] = useState([]);
  const [generateCoversheet, setGenerateCoversheet] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [reviewerSearchValues, setReviewerSearchValues] = useState({});
  const [ccSearchValue, setCcSearchValue] = useState("");
  const statusValue = Form.useWatch("status", form);
  const releasedOnValue = Form.useWatch("released_on", form);
  const estimatedDeliveryLeadTimeValue = Form.useWatch(
    "estimated_delivery_lead_time_weeks",
    form
  );
  const attachmentsValue = Form.useWatch("attachments", form);
  const coversheetEnabled = hasPdfAttachments(attachmentsValue);
  const emailNotificationsDisabled = ["Draft", "Closed"].includes(statusValue);
  const trackMaterialRelease = normalizeBooleanValue(
    selectedProject?.track_material_release
  );
  const calculatedEstimatedDeliveryDate = calculateDateFromLeadTime(
    releasedOnValue,
    estimatedDeliveryLeadTimeValue
  );

  const { data: users } = useQuery({
    queryKey: ["projectUsers", projectId, 1, 1000],
    queryFn: () => fetchProjectUsers(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const { data: vendors } = useQuery({
    queryKey: ["vendors", projectId, 1, 1000],
    queryFn: () => fetchVendors(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const { data: specSections } = useQuery({
    queryKey: ["specSections", projectId],
    queryFn: () => fetchSpecSections(projectId),
    enabled: !!projectId,
  });

  const mutation = useMutation({
    mutationFn: ({ submittalId, submittalData, sendEmail, coversheetId }) =>
      updateSubmittal(submittalId, submittalData, sendEmail, coversheetId),
    onSuccess: () => {
      queryClient.invalidateQueries(["submittals", projectId]);
      queryClient.invalidateQueries(["submittal", submittalId]);
      refetch();
      onClose();
      form.resetFields();
      setReviewerSearchValues({});
      setCcSearchValue("");
      notification.success({ message: "Submittal updated successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update submittal",
        description: error.response.data.detail.message,
      });
    },
  });

  const coversheetMutation = useMutation({
    mutationFn: ({ submittalData, submittalId }) =>
      createSubmittalCoversheet(submittalData, submittalId),
    onError: (error) => {
      notification.error({
        message: "Failed to create coversheet",
        description: error.response?.data?.detail?.message || "Unknown error",
      });
    },
  });

  const { data: submittal, refetch } = useQuery({
    queryKey: ["submittal", submittalId],
    queryFn: () => getSubmittalById(submittalId),
    enabled: !!submittalId,
    select: (data) => ({
      ...data,
      formatted_number: data.specification_section_details?.section_number
        ? `${data.specification_section_details.section_number}-${data.number}`
        : data.number,
      formatted_ball_in_courts: data?.ball_in_court_details
        ?.map((element) => element.username)
        .join(", "),
      formatted_received_from: data.received_from_details?.username,
      formatted_spec_section:
        data.specification_section_details?.title || "N/A",
    }),
    onError: (error) => {
      console.log("Failed to fetch submittal", error);
    },
  });

  useEffect(() => {
    if (visible) {
      refetch();
    }
  }, [visible, refetch]);

  useEffect(() => {
    if (submittal) {
      const initialSteps = submittal.steps?.map((step) => ({
        reviews: step.reviews?.map((review) => ({
          review_id: review._id || null,
          reviewer_id: review.reviewer_id,
        })),
      }));
      setReviewSteps(initialSteps);

      const formValues = {
        title: submittal.title,
        description: submittal.description,
        type: submittal.type || undefined,
        edms: submittal.edms || undefined,
        due_date: submittal.due_date ? parseDateOnlyForPicker(submittal.due_date) : null,
        issue_date: submittal.issue_date ? parseDateOnlyForPicker(submittal.issue_date) : null,
        released_on: submittal.released_on
          ? parseDateOnlyForPicker(submittal.released_on)
          : null,
        estimated_delivery_lead_time_weeks: calculateLeadTimeWeeks(
          submittal.released_on,
          submittal.estimated_delivery_date
        ),
        received_from_id: submittal.received_from_id,
        responsible_contractor_id: submittal.responsible_contractor_id,
        submitted_for: submittal.submitted_for || "For Approval",
        specification_section_id: submittal.specification_section_id,
        submittal_manager_id: submittal.submittal_manager_id,
        number: submittal.number,
        revision: submittal.revision,
        status: submittal.status,
        priority: submittal.priority || "Medium",
        steps: submittal.steps?.map((step) => ({
          reviews: step.reviews?.map((review) => review.reviewer_id),
        })),
        distribution_list: submittal.distribution_list,
        attachments: submittal.original_attachments?.length
          ? submittal.original_attachments
          : submittal.attachments || [],
      };
      form.resetFields();
      form.setFieldsValue(formValues);
      const initialSendEmail = !["Draft", "Closed"].includes(submittal.status);
      setSendEmail(initialSendEmail);
      form.setFieldsValue({ email_notifications: initialSendEmail });
    }
  }, [submittal, form]);

  useEffect(() => {
    if (emailNotificationsDisabled) {
      setSendEmail(false);
      form.setFieldsValue({ email_notifications: false });
    }
  }, [emailNotificationsDisabled, form]);

  useEffect(() => {
    if (!coversheetEnabled && generateCoversheet) {
      setGenerateCoversheet(false);
      form.setFieldsValue({ generate_coversheet: false });
    }
  }, [coversheetEnabled, generateCoversheet, form]);

  const handleChangeReviewer = (values, index) => {
    let _reviewSteps = [...reviewSteps];
    const reviewers = _reviewSteps[index].reviews.map(
      (review) => review.reviewer_id,
    );
    const isAddition = values.some(
      (reviewer_id) => !reviewers.includes(reviewer_id),
    );

    if (isAddition) {
      _reviewSteps[index].reviews = [
        ..._reviewSteps[index].reviews,
        {
          reviewer_id: values.find((element) => !reviewers.includes(element)),
          review_id: null,
        },
      ];
    } else {
      _reviewSteps[index].reviews = _reviewSteps[index].reviews.filter(
        (review) => values.includes(review.reviewer_id),
      );
    }

    setReviewSteps(_reviewSteps);
  };

  const handleRemoveStep = (index) => {
    let _reviewSteps = [...reviewSteps];
    _reviewSteps.splice(index, 1);

    setReviewSteps(_reviewSteps);
  };

  const handleAddSubmittal = () => {
    console.log(reviewSteps);

    let _reviewSteps = [...reviewSteps, { reviews: [] }];

    setReviewSteps(_reviewSteps);
  };

  const handleUpdateSubmittal = async (values) => {
    const submittalData = {
      ...values,
      project_id: projectId,
      ...(selectedProject?.use_custom_terminology && {
        type: "Submittal",
      }),
      due_date: values.due_date ? toUTCMidnight(values.due_date) : null,
      issue_date: values.issue_date ? toUTCMidnight(values.issue_date) : null,
      released_on: values.released_on ? toUTCMidnight(values.released_on) : null,
      estimated_delivery_date: calculatedEstimatedDeliveryDate
        ? toUTCMidnight(calculatedEstimatedDeliveryDate)
        : null,
      steps: reviewSteps?.map((step) => ({
        reviews: step.reviews?.map((review) => ({
          reviewer_id: review.reviewer_id,
          id: review.review_id,
        })),
      })),
      attachments: values.attachments?.map((file) => file) || null,
    };

    try {
      if (generateCoversheet) {
        const coversheetResponse = await coversheetMutation.mutateAsync({
          submittalData,
          submittalId,
        });
        mutation.mutate({
          submittalId: submittalId,
          submittalData,
          coversheetId: coversheetResponse.coversheet_id,
          sendEmail: sendEmail,
        });
      } else {
        mutation.mutate({
          submittalId: submittalId,
          submittalData,
          coversheetId: null,
          sendEmail: sendEmail,
        });
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleSpecSectionChange = (value) => {
    fetchNextSubmittalNumber(projectId, value).then((data) => {
      form.setFieldsValue({ number: data.next_number });
    });
  };

  const handleReceivedFromChange = (value) => {
    const receivedFrom = users?.data?.find((user) => user.id === value);
    if (receivedFrom?.vendor_id) {
      form.setFieldsValue({
        responsible_contractor_id: receivedFrom.vendor_id,
      });
    } else {
      // Clear the responsible contractor field if user has no vendor_id
      form.setFieldsValue({
        responsible_contractor_id: undefined,
      });
    }
  };

  const handleClose = () => {
    if (form.isFieldsTouched()) {
      Modal.confirm({
        title: "Unsaved Changes",
        content:
          "You have unsaved changes. Are you sure you want to close without saving?",
        okText: "Yes, Close",
        okType: "danger",
        cancelText: "Cancel",
        onOk: () => {
          form.resetFields();
          setReviewerSearchValues({});
          setCcSearchValue("");
          onClose();
        },
      });
    } else {
      form.resetFields();
      setReviewerSearchValues({});
      setCcSearchValue("");
      onClose();
    }
  };

  return (
    <Modal
      title="Update Submittal"
      open={visible}
      onCancel={handleClose}
      footer={null}
      className="w-full max-w-4xl"
    >
      {!submittal ? (
        <Skeleton active />
      ) : (
        <Form form={form} onFinish={handleUpdateSubmittal} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Number"
                name="number"
                rules={[
                  {
                    required: true,
                    message: "Please input the submittal number!",
                  },
                  {
                    pattern: /^\d+$/,
                    message: "Please enter only whole numbers!",
                  },
                ]}
              >
                <Input
                  type="number"
                  step="1"
                  onKeyDown={handleIntegerKeyDown}
                  onChange={createIntegerChangeHandler(form, "number")}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Revision"
                name="revision"
                rules={[
                  { required: true, message: "Please input the revision!" },
                  {
                    pattern: /^\d+$/,
                    message: "Please enter only whole numbers!",
                  },
                ]}
              >
                <Input
                  type="number"
                  step="1"
                  onKeyDown={handleIntegerKeyDown}
                  onChange={createIntegerChangeHandler(form, "revision")}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: "Please input the title!" }]}
          >
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Specification Section"
                name="specification_section_id"
                getValueFromEvent={(value) => {
                  handleSpecSectionChange(value);
                  return value;
                }}
              >
                <SpecSectionSelect />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Submittal Manager"
                name="submittal_manager_id"
                rules={[
                  {
                    required: true,
                    message: "Please select the submittal manager!",
                  },
                ]}
              >
                <ProjectUserSelect
                  mode={null}
                  placeholder="Select submittal manager"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: "Please select the status!" }]}
              >
                <Select>
                  <Select.Option value="Open">Open</Select.Option>
                  <Select.Option value="Closed">Closed</Select.Option>
                  <Select.Option value="Draft">Draft</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Priority" name="priority" className="w-full">
                <Select
                  options={[
                    { value: "High", label: "High" },
                    { value: "Medium", label: "Medium" },
                    { value: "Low", label: "Low" },
                  ]}
                  className="w-full"
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Description" name="description" className="w-full">
            <SimpleRichTextEditor
              placeholder="Enter description..."
              aiEnhanceTarget="question"
            />
          </Form.Item>
          {selectedProject?.use_custom_terminology && (
            <Form.Item label="EDMS" name="edms">
              <Input />
            </Form.Item>
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Issue Date" name="issue_date" className="w-full">
                <DatePicker className="w-full" format={DATE_PICKER_FORMAT} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Due Date" name="due_date" className="w-full">
                <DatePicker className="w-full" format={DATE_PICKER_FORMAT} />
              </Form.Item>
            </Col>
          </Row>
          {trackMaterialRelease && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Released On" name="released_on">
                  <DatePicker className="w-full" format={DATE_PICKER_FORMAT} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Estimate Lead Time (in weeks)"
                  name="estimated_delivery_lead_time_weeks"
                  extra={
                    calculatedEstimatedDeliveryDate ? (
                      <span className="text-xs text-gray-500">
                        {calculatedEstimatedDeliveryDate.format(
                          DATE_PICKER_FORMAT
                        )}
                      </span>
                    ) : null
                  }
                >
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    placeholder="Weeks"
                    onKeyDown={handleIntegerKeyDown}
                    onChange={createIntegerChangeHandler(
                      form,
                      "estimated_delivery_lead_time_weeks"
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Received From"
                name="received_from_id"
                getValueFromEvent={(value) => {
                  handleReceivedFromChange(value);
                  return value;
                }}
              >
                <ProjectUserSelect
                  mode={null}
                  allowClear
                  placeholder="Select received from"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Submitted For"
                name="submitted_for"
                initialValue="For Approval"
              >
                <Select
                  options={[
                    { value: "For Approval", label: "For Approval" },
                    { value: "For Record", label: "For Record" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Cc" name="distribution_list" className="w-full">
            <ProjectUserSelect
              searchValue={ccSearchValue}
              onSearch={setCcSearchValue}
              allowClear
              placeholder="Select Cc"
              className="w-full"
            />
          </Form.Item>
          <Form.Item name="attachments" label="Attachments" className="w-full">
            <AttachmentsSection projectId={projectId} />
          </Form.Item>
          <Form.Item>
            {reviewSteps.map((step, index) => {
              return (
                <div className="my-5" key={index}>
                  <label className="mr-2">Step #{index + 1} Assignees</label>
                  <div className="flex items-center">
                    <ProjectUserSelect
                      mode="multiple"
                      placeholder="Select assignees"
                      searchValue={reviewerSearchValues[index] || undefined}
                      onSearch={(value) => {
                        setReviewerSearchValues((prev) => ({
                          ...prev,
                          [index]: value,
                        }));
                      }}
                      value={step.reviews.map((review) => review.reviewer_id)}
                      onChange={(value) => {
                        handleChangeReviewer(value, index);
                      }}
                      className="flex-grow"
                    />
                    <Button
                      type="danger"
                      icon={<CloseOutlined />}
                      onClick={() => {
                        handleRemoveStep(index);
                        setReviewerSearchValues((prev) => {
                          const newValues = { ...prev };
                          delete newValues[index];
                          const reindexed = {};
                          Object.keys(newValues).forEach((key) => {
                            const keyNum = parseInt(key);
                            if (keyNum > index) {
                              reindexed[keyNum - 1] = newValues[key];
                            } else if (keyNum < index) {
                              reindexed[keyNum] = newValues[key];
                            }
                          });
                          return reindexed;
                        });
                      }}
                      className="ml-2"
                    />
                  </div>
                </div>
              );
            })}
            <Button type="dashed" onClick={handleAddSubmittal} className="w-full">
              Add Review Step
            </Button>
          </Form.Item>
          <div className="project-settings-grid mt-10">
            <Tooltip
              title={
                !coversheetEnabled ? COVERSHEET_REQUIRES_PDF_TOOLTIP : null
              }
            >
              <ProjectSettingToggle name="generate_coversheet" title="Generate Coversheet" disabled={!coversheetEnabled} onChange={setGenerateCoversheet} />
            </Tooltip>
            <ProjectSettingToggle name="email_notifications" title="Email Notifications" description={emailNotificationsDisabled ? "Set status to Open to enable emails" : undefined} initialValue={true} checked={sendEmail} disabled={emailNotificationsDisabled} onChange={setSendEmail} />
          </div>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={mutation.isPending || coversheetMutation.isPending}
              style={{ marginTop: "16px", width: "100%" }}
            >
              {sendEmail
                ? "Update Submittal and Send Emails"
                : "Update Submittal"}
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default UpdateSubmittalModal;
